type PartialBOMSearch = Partial<Omit<BOMSearch, "Parts">> & { Parts: Partial<BOMSearchPart>[] };

// Partial BOM is suitable for sending to API
export function getPartialBom(bom: BOMSearch, skipParts: boolean = false): PartialBOMSearch {
    const { HashId, Name, Distributors, SortOrder, StockFilter, BatchSize, Parts: parts } = bom;
    const partialParts = Array.isArray(parts) && !skipParts ? parts.map(getPartialPart) : [];
    return { HashId, Name, Distributors, SortOrder, StockFilter, BatchSize, Parts: partialParts };
}

export function getPartialPart(part: Partial<BOMSearchPart>): Partial<BOMSearchPart> {
    const {
        Id,
        Key,
        PartId,
        PartNumber,
        ManufacturerId,
        InternalReferenceNumber,
        Quantity,
        SortNum,
        Selections: selections,
    } = part;

    return {
        Id,
        Key,
        PartId,
        PartNumber,
        ManufacturerId,
        InternalReferenceNumber,
        Quantity,
        SortNum,
        Selections: selections?.map(({ Quantity, DistributorId, DistributorPartNumber }) => ({
            Quantity,
            DistributorId,
            DistributorPartNumber,
        })),
    };
}

const bomSortFunctions: Partial<Record<BOMSortOrder, (part1: BOMSearchPart, part2: BOMSearchPart) => number>> = {
    [BOMSortOrder.Default]: (part1: BOMSearchPart, part2: BOMSearchPart) => (part1.SortNum ?? 0) - (part2.SortNum ?? 0),
    [BOMSortOrder.Manufacturer_Asc]: (part1: BOMSearchPart, part2: BOMSearchPart) =>
        (part1.ManufacturerName ?? "zzz").localeCompare(part2.ManufacturerName ?? "zzz"),
    [BOMSortOrder.Manufacturer_Desc]: (part1: BOMSearchPart, part2: BOMSearchPart) =>
        (part2.ManufacturerName ?? "zzz").localeCompare(part1.ManufacturerName ?? "zzz"),
    [BOMSortOrder.PartNum_Asc]: (part1: BOMSearchPart, part2: BOMSearchPart) =>
        (part1.PartNumber ?? "zzz").localeCompare(part2.PartNumber ?? "zzz"),
    [BOMSortOrder.PartNum_Desc]: (part1: BOMSearchPart, part2: BOMSearchPart) =>
        (part2.PartNumber ?? "zzz").localeCompare(part1.PartNumber ?? "zzz"),
};

export function getBOMSortFunction(sortOrder: BOMSortOrder, partEmissions?: Record<string, number | null>) {
    const sortFunction = bomSortFunctions[sortOrder];
    if (sortFunction) return sortFunction;
    if (!partEmissions) return;

    const getPartEmission = (part: BOMSearchPart) => partEmissions[part.Key] ?? 0;

    if (sortOrder == BOMSortOrder.KgCO2e_Asc) {
        return (part1: BOMSearchPart, part2: BOMSearchPart) => getPartEmission(part1) - getPartEmission(part2);
    } else if (sortOrder == BOMSortOrder.KgCO2e_Desc) {
        return (part1: BOMSearchPart, part2: BOMSearchPart) => getPartEmission(part2) - getPartEmission(part1);
    }
}

const bomSortFieldNames: Record<BOMSortOrder, string> = {
    [BOMSortOrder.Default]: "default",
    [BOMSortOrder.PartNum_Asc]: "part_number_asc",
    [BOMSortOrder.PartNum_Desc]: "part_number_desc",
    [BOMSortOrder.Manufacturer_Asc]: "manufacturer_asc",
    [BOMSortOrder.Manufacturer_Desc]: "manufacturer_desc",
    [BOMSortOrder.KgCO2e_Asc]: "kg_co2e_asc",
    [BOMSortOrder.KgCO2e_Desc]: "kg_co2e_desc",
};

export function getBOMSortEventName(sortOrder: BOMSortOrder) {
    const field = bomSortFieldNames[sortOrder] ?? "unknown";
    return `sort_${field}`;
}

export function getBOMCoverageSorter(coverageValues: Record<number, number>) {
    const getCoverage = (id: number) => coverageValues[id] ?? 0;

    return (a: BOMSearchDistributor, b: BOMSearchDistributor) => {
        let result = getCoverage(b.Id) - getCoverage(a.Id);
        if (result === 0) result = a.Name.localeCompare(b.Name);
        return result;
    };
}

export function getPriceByQuantity(prices?: PartResultPrice[], quantity?: number) {
    if (Array.isArray(prices) && prices.length > 0) {
        const [first] = prices;
        const last = prices.findLast((price) => (price.Quantity ?? 0) <= (quantity ?? 0));
        return last ? last : first;
    }
    return null;
}

export function getFilteredBOMParts(
    parts: BOMSearchPart[],
    filters: BOMFilters,
    removedIds: Record<number, boolean>,
    coverageParts: Record<string, BOMCoveragePart>
) {
    const filterFunctions: ((part: BOMSearchPart) => boolean)[] = [];

    const upperPartNumber = filters.partNumber?.trim().toUpperCase();
    if (upperPartNumber) {
        filterFunctions.push((part: BOMSearchPart) => part.PartNumber.includes(upperPartNumber));
    }

    const upperMfrName = filters.manufacturerName?.trim().toUpperCase();
    if (upperMfrName) {
        filterFunctions.push(
            (part: BOMSearchPart) =>
                !!part.ManufacturerName && part.ManufacturerName.toUpperCase().includes(upperMfrName)
        );
    }

    if (Object.keys(removedIds).length > 0) {
        filterFunctions.push((part: BOMSearchPart) => !removedIds[part.Id]);
    }

    if (filters.availability === "in-stock") {
        filterFunctions.push((part: BOMSearchPart) => coverageParts[part.Key]?.InStock === true);
    } else if (filters.availability === "out-of-stock") {
        filterFunctions.push((part: BOMSearchPart) => coverageParts[part.Key]?.InStock === false);
    }

    if (filters.lifecycleRisk !== undefined) {
        filterFunctions.push((part: BOMSearchPart) => coverageParts[part.Key]?.LifecycleRisk === filters.lifecycleRisk);
    }

    if (filters.supplyChainRisk !== undefined) {
        filterFunctions.push(
            (part: BOMSearchPart) => coverageParts[part.Key]?.SupplyChainRisk === filters.supplyChainRisk
        );
    }

    if (filters.tariff === true) {
        filterFunctions.push((part: BOMSearchPart) => coverageParts[part.Key]?.IsAffectedByTariff === true);
    }

    if (filters.unmatched === true) {
        filterFunctions.push((part: BOMSearchPart) => !part.PartId);
    }

    return filterFunctions.length > 0 ? parts.filter((part) => filterFunctions.every((fn) => fn(part))) : parts;
}

export function getDistributorProps(
    responsePart: BOMSearchResponsePart | null | undefined,
    selections: Record<number, BOMSearchSelection | null>,
    otherProps?: Partial<BomTableDistributorProps>
) {
    const output: Record<number, Omit<BomTableDistributorProps, "distributor">> = {};

    if (responsePart && Array.isArray(responsePart.DistributorResults)) {
        // use server sort order (best stock descending)
        const sortedResults = responsePart.DistributorResults.toSorted((resultA, resultB) =>
            resultA.SortOrder.localeCompare(resultB.SortOrder)
        );

        for (const { Distributor: dist, ...partResult } of sortedResults) {
            const existing = output[dist.Id];
            if (existing && existing.others) {
                const selectionPartNum = selections[dist.Id]?.DistributorPartNumber;
                if (
                    selectionPartNum &&
                    selectionPartNum === partResult.DistributorPartNumber &&
                    selectionPartNum !== existing.result?.DistributorPartNumber
                ) {
                    // this means the selection is not the default part result, swap them
                    if (existing.result) existing.others.push(existing.result);
                    existing.result = partResult;
                } else {
                    existing.others.push(partResult);
                }
            } else {
                output[dist.Id] = { result: partResult, others: [], selection: selections[dist.Id], ...otherProps };
            }
        }
    }

    return output;
}
